import { AutomationActionType } from "@/types/graphql/partner.generated";

/**
 * Строка списка для массовых действий: что с ней можно сделать, решает
 * сервер (Automation.allowedActions, таблица automations/lifecycle.ts,
 * которой проверяет и deleteAutomations). Архив, наоборот, не ограничен
 * статусом: архив и есть способ остановить автоматизацию.
 */
export interface AutomationBulkRow {
  id: string;
  allowedActions: readonly AutomationActionType[];
}

export interface AutomationBulkSelection {
  /** Все выбранные id; в архив уходят все (архив не ограничен статусом). */
  selectedIds: string[];
  /** Подмножество, которое реально удалится (DELETE в allowedActions). */
  deletableIds: string[];
  /** Сколько выбранных не попадёт в удаление. */
  skippedOnDeleteCount: number;
}

/**
 * Чистая функция выбора для тулбара списка автоматизаций: что уйдёт в
 * архив (всё выбранное) и что реально удалится (строки, которым сервер
 * разрешает DELETE: бэкенд отклоняет весь запрос целиком, если среди id
 * есть строка, которую удалять нельзя). Вынесена из
 * AutomationsList.tsx/AutomationsListToolbar.tsx, чтобы протестировать
 * все комбинации статусов без рендера компонентов.
 */
export function automationBulkSelection(
  rows: AutomationBulkRow[],
  selected: Record<string, boolean>
): AutomationBulkSelection {
  const selectedRows = rows.filter((row) => selected[row.id]);
  const selectedIds = selectedRows.map((row) => row.id);
  const deletableIds = selectedRows
    .filter((row) =>
      row.allowedActions.includes(AutomationActionType.DeleteType)
    )
    .map((row) => row.id);
  return {
    selectedIds,
    deletableIds,
    skippedOnDeleteCount: selectedIds.length - deletableIds.length,
  };
}

export interface AutomationDeleteConfirm {
  /** id, которые реально уйдут в мутацию deleteAutomations. */
  ids: string[];
  /** Есть ли что удалять вообще: кнопка активна. */
  canDelete: boolean;
  /** Показывать ли в тексте подтверждения строку про пропущенные автоматизации. */
  showSkipWarning: boolean;
}

/**
 * Что покажет диалог подтверждения массового удаления и что уйдёт в
 * мутацию. Чистая функция: не зависит от i18n/JSX, поэтому тестируется
 * без рендера тулбара (текст собирает уже сам компонент по этим флагам).
 */
export function automationDeleteConfirm(
  selection: AutomationBulkSelection
): AutomationDeleteConfirm {
  return {
    ids: selection.deletableIds,
    canDelete: selection.deletableIds.length > 0,
    showSkipWarning: selection.skippedOnDeleteCount > 0,
  };
}
